import React, { useCallback, useMemo, useRef } from 'react';
import { Clock, Lock } from 'lucide-react';
import { useBoardsUiStore } from '../extensions/bundled/task-manager/boardsUiStore';
import { activeFilterCount, filtersActive } from '../extensions/bundled/task-manager/boardFilters';
import { filterChipCounts, unfilteredTasks } from '../extensions/bundled/task-manager/boardFilterCounts';
import { pillColors } from '../extensions/bundled/task-manager/boardLabels';
import BoardFilterSearch from './boards/BoardFilterSearch';
import BoardFilterChip from './boards/BoardFilterChip';
import BoardPriorityFilter from './boards/BoardPriorityFilter';
import BoardAssigneeFilter, { assigneeFilterValue } from './boards/BoardAssigneeFilter';
import BoardLabelsFilter from './boards/BoardLabelsFilter';
import useLabelChipOverflow from './boards/useLabelChipOverflow';
import Tooltip from './ui/Tooltip';
import './boards/BoardFilterBar.css';

/**
 * The one-row filter bar above an open board, in both kanban and list mode
 * (Boards Redesign list 4, cards 4.1-4.3):
 *
 *   [search  /] | FILTER  (Overdue 3) (Priority ▾) (Assignee ▾) (● label 6)…  Clear 2
 *                                                     7 of 25 shown  [🔒 Reordering off while filtered]
 *
 * The matching lives in boardFilters.js and the chip counts in
 * boardFilterCounts.js; this component only edits filter state and lays the
 * pieces out. The search box, a chip and the priority picker are their own
 * files under boards/.
 *
 * The assignee picker (Mine / Unassigned) is hidden on a private
 * board: it has no members, every task is the owner's, so both would be
 * either a no-op or a way to empty the board for no reason.
 */
function BoardFilterBar({
  isTeamBoard = false,
  shown = 0,
  total = 0,
  palette = [],
  tasks = [],
  showDoneTasks = true,
  currentUserId = null,
  hotkeyActive = true,
}) {
  const filters = useBoardsUiStore((s) => s.filters);
  const setFilters = useBoardsUiStore((s) => s.setFilters);
  const resetFilters = useBoardsUiStore((s) => s.resetFilters);
  const active = filtersActive(filters);
  const activeCount = activeFilterCount(filters);
  const counts = useMemo(
    () => filterChipCounts(unfilteredTasks(tasks, showDoneTasks), { currentUserId }),
    [tasks, showDoneTasks, currentUserId]
  );
  const commitSearch = useCallback((search) => setFilters({ search }), [setFilters]);

  const toggle = (key) => setFilters({ [key]: !filters[key] });
  const selectedLabels = filters.labels || [];

  // One row, like the artboard: label chips that do not fit fold into "+N".
  const barRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const folded = useLabelChipOverflow({
    barRef,
    leftRef,
    rightRef,
    labels: palette.map((l) => ({ id: l.id, on: selectedLabels.includes(l.id) })),
  });

  return (
    <div className="task-board-filter-bar" ref={barRef}>
      <div className="board-filter-left" ref={leftRef}>
        <BoardFilterSearch value={filters.search || ''} onCommit={commitSearch} hotkeyActive={hotkeyActive} />
        <span className="board-filter-divider" aria-hidden />
        <span className="board-filter-caption" aria-hidden>
          Filter
        </span>

        <BoardFilterChip
          label="Overdue"
          tone="danger"
          icon={<Clock size={12} strokeWidth={2.2} aria-hidden />}
          count={counts.overdue}
          on={!!filters.overdue}
          onToggle={() => toggle('overdue')}
        />
        <BoardPriorityFilter
          value={filters.priorityLevel || ''}
          counts={counts.priority}
          onChange={(priorityLevel) => setFilters({ priorityLevel })}
        />
        {isTeamBoard && (
          <BoardAssigneeFilter
            value={assigneeFilterValue(filters)}
            counts={{ mine: counts.mine, unassigned: counts.unassigned }}
            onChange={(v) => setFilters({ mine: v === 'mine', unassigned: v === 'unassigned' })}
          />
        )}

        {/* The board's own labels as toggles, rather than a dropdown: a board
            has a handful, they are already colour-coded, and a dropdown would
            hide the one piece of filter state you cannot infer from the chip
            row. Selecting several means ANY of them (see matchesLabels).
            Nothing renders when the board has no palette. */}
        {/* Not enough room for every chip → ONE "Labels" dropdown with
            checkboxes instead of chips + "+N" (owner, 13.10.2026). The chips
            come back as soon as they all fit again (cached widths). */}
        {/* Also rendered (hidden) while the chips fit: the compact toolbar
            (useToolbarCompact) shows it instead of the chips, whose names it
            hides — bare colour dots would say nothing. */}
        {palette.length > 0 && (
          <BoardLabelsFilter
            standby={folded.length === 0}
            palette={palette}
            counts={counts.labels}
            selected={selectedLabels}
            onChange={(labels) => setFilters({ labels })}
          />
        )}
        {folded.length === 0 && palette.map((l) => {
          const on = selectedLabels.includes(l.id);
          return (
            <BoardFilterChip
              key={l.id}
              labelId={l.id}
              label={l.name}
              dot={pillColors(l).dot}
              count={counts.labels[l.id] || 0}
              on={on}
              removable
              onToggle={() =>
                setFilters({ labels: on ? selectedLabels.filter((x) => x !== l.id) : [...selectedLabels, l.id] })
              }
            />
          );
        })}

        {activeCount > 0 && (
          <button type="button" className="board-filter-reset" onClick={resetFilters}>
            Clear {activeCount}
          </button>
        )}
      </div>

      <div className="board-filter-right" ref={rightRef}>
        {/* Only while filtering (owner, 13.10.2026: no bare "121 tasks" on an
            unfiltered board). Never just "3" — a filtered board that shows a
            bare count reads as an almost-empty board. */}
        {active && (
          <span className="task-board-filter-count">
            <b>{shown}</b> of {total} shown
          </span>
        )}

        {active && (
          <Tooltip content="Clear the filters to reorder cards" side="bottom">
            <span className="task-board-filter-locked" tabIndex={0} aria-label="Reordering off while filtered">
              <Lock size={11} strokeWidth={2.2} aria-hidden />
              {/* Hidden inside the one-row toolbar (BoardToolbar.css): the lock
                  and its tooltip carry it there; the text needs ~170 px. */}
              <span className="task-board-filter-locked-text" aria-hidden> Reordering off while filtered</span>
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

// Memoised (big boards): its layout effect measures the chips on every render,
// so it must not re-render on drag steps (TaskBoardTab passes move-stable tasks).
export default React.memo(BoardFilterBar);
